/**
 * Registro centrale degli "artifact type" TL4K (elaborati docente/sessione).
 *
 * Perché esiste: TeacherArtifacts.tsx e MyArtifacts.tsx duplicavano la stessa
 * conoscenza per-app (env var per l'URL dell'editor, path API di sessione,
 * path API del portfolio personale, capacità come il rename) sotto forma di
 * if/else e ternari annidati. Onboardare una nuova app significava modificare
 * gli interni dei componenti condivisi. Con questo registro, onboardare una
 * nuova app è una entry di dati, non una modifica di codice.
 *
 * I campi sono stati derivati dal codice REALE (non inventati): vedi i commit
 * precedenti di TeacherArtifacts.tsx (getAppOpenUrl) e MyArtifacts.tsx
 * (fetchPage, MY_ARTIFACTS_TYPES, renameEnabled, i vari get*OpenUrl).
 */

/** Le app che oggi salvano un "artefatto docente/sessione" tramite
 *  collaboration-service. Chiave stabile usata come `artifact_type` a DB. */
export type ArtifactTypeKey =
  | 'wordcloud'
  | 'scratchjr'
  | 'spirografo'
  | 'disegna'
  | 'svg-edit'
  | 'qns'
  | 'techyblocks'
  | 'scrivi'
  | 'calcola'
  | 'circuiti-sim'
  | 'book-creator'
  | 'mo-mappe'
  | 'pixel-art'
  | 'story-dice';

/** Come costruire il deep-link "apri nell'app" per un artefatto.
 *  Precedenza: prima env var che risulta valorizzata (in ordine), poi
 *  fallback su `window.location.origin`. */
export interface ArtifactOpenUrlConfig {
  /** Nomi delle env var Vite da provare in ordine (VITE_*). */
  envKeys: string[];
  /** Path da appendere alla base quando questa viene da una env var
   *  (es. '/editor.html' per techyblocks). Stringa vuota = nessun path. */
  path: string;
  /** Path da appendere quando si ricade sull'origin (nessuna env var
   *  valorizzata). Se omesso, usa lo stesso valore di `path`. */
  originFallbackPath?: string;
  /** Se presente, il path di fallback va letto da QUESTA env var (con
   *  `originFallbackPath` come default se la env var non è valorizzata).
   *  Serve solo a wordcloud, che legge `BASE_PATH`. */
  originFallbackPathEnvKey?: string;
}

export interface ArtifactTypeDefinition {
  key: ArtifactTypeKey;
  /** Etichetta umana, usata sia nel selettore `MY_ARTIFACTS_TYPES` sia nei
   *  bottoni "Apri in <label>" di entrambi i componenti. */
  label: string;
  /** Config del deep-link "apri nell'app": usata sia da TeacherArtifacts
   *  (query param `teacher_artifact_id`) sia da MyArtifacts (query param
   *  `artifact_id`) — la base URL è identica, cambia solo il param. */
  openUrl: ArtifactOpenUrlConfig;
  /** true se questo tipo espone davvero un bottone "Apri in ..." nella
   *  griglia di MyArtifacts. QNS non lo espone (nessun caso in ArtifactGrid
   *  nel codice originale): mantenuto false per non introdurre un link mai
   *  esistito. */
  studentOpenUrlEnabled: boolean;
  /** Segmento API per gli artefatti di sessione:
   *  `${COLLAB_API_BASE}/${sessionApiPath}` (fetchPage di MyArtifacts) e
   *  `${COLLAB_API_BASE}/${sessionApiPath}/${id}` (PATCH rename). */
  sessionApiPath: string;
  /** Segmento API per il portfolio personale (studente, non-teacherMode):
   *  `${COLLAB_API_BASE}/${portfolioApiPath}/${pseudonymIdentityId}`. */
  portfolioApiPath: string;
  /** Rinomina disponibile in console docente (oggi vero solo per
   *  circuiti-sim, che ha una PATCH dedicata). */
  renameEnabled: boolean;
  /** Mostra il bottone "Dettaglio (parole)" nella card — solo wordcloud. */
  supportsWordDetail?: boolean;
  /** true (default) se il link "Apri in ..." deve aprire in una nuova tab
   *  (`target="_blank" rel="noopener noreferrer"`). TechyBlocks nel codice
   *  originale (ArtifactGrid di MyArtifacts.tsx) non aveva questi attributi:
   *  comportamento preservato qui, non un'omissione della migrazione. */
  openInNewTab?: boolean;
  /** true se l'app adotta il contratto di assegnazione docente→classe
   *  (docs/standards/TL4K_TEACHER_ARTIFACT_ASSIGNMENT.md). Il pannello
   *  `TeacherArtifactAssignPanel` va montato solo per le app assignable. */
  assignable?: boolean;
  /** Prefisso API delle assegnazioni. Valore del contratto comune: `sessions`
   *  (PUT/GET/DELETE `${COLLAB_API_BASE}/sessions/:sessionId/artifact-assignment`).
   *  Solo un'app con route proprie (es. diario, fino al retrofit) ne dichiara
   *  un altro. L'inventario delle ISTANZE sta nel path di sessione dell'app:
   *  `sessionApiPath` qui sopra. */
  assignmentApiPath?: string;
  /** Bootstrap studente dell'assegnazione:
   *  `${COLLAB_API_BASE}/<bootstrapApiPath>?session_id=&pin=&pseudonym_identity_id=`. */
  bootstrapApiPath?: string;
  /** true se il tipo ha un adapter di export in collab-service (lib/artifactShare/adapters.ts):
   *  solo allora "Scarica tutti" (zip degli elaborati filtrati) compare nella griglia docente.
   *  Oggi solo svg-edit; gli altri si aggiungono un adapter alla volta. */
  exportable?: boolean;
}

export const ARTIFACT_REGISTRY: Record<ArtifactTypeKey, ArtifactTypeDefinition> = {
  wordcloud: {
    key: 'wordcloud',
    label: 'WordCloud',
    openUrl: {
      envKeys: ['VITE_WORDCLOUD_APP_URL', 'VITE_WORDCLOUD_URL'],
      path: '',
      // Fallback storico: origin + BASE_PATH (default '/tl4k-wordcloud/').
      originFallbackPath: '/tl4k-wordcloud/',
      originFallbackPathEnvKey: 'BASE_PATH',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/wordcloud',
    portfolioApiPath: 'artifacts/portfolio',
    renameEnabled: false,
    supportsWordDetail: true,
  },
  scratchjr: {
    key: 'scratchjr',
    label: 'ScratchJr',
    openUrl: {
      envKeys: ['VITE_SCRATCHJR_APP_URL'],
      path: '',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/scratchjr',
    portfolioApiPath: 'artifacts/portfolio-sjr',
    renameEnabled: false,
  },
  spirografo: {
    key: 'spirografo',
    label: 'Spirografo',
    openUrl: {
      envKeys: ['VITE_SPIROGRAFO_APP_URL', 'VITE_SPIROGRAFO_URL'],
      path: '',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/spirografo',
    portfolioApiPath: 'artifacts/portfolio-spiro',
    renameEnabled: false,
  },
  disegna: {
    key: 'disegna',
    label: 'Disegna',
    openUrl: {
      // Ordine storico: VITE_DISEGNA_URL prima di VITE_DISEGNA_APP_URL
      // (getDisegnaOpenUrl in MyArtifacts.tsx), diverso dagli altri tipi.
      envKeys: ['VITE_DISEGNA_URL', 'VITE_DISEGNA_APP_URL'],
      path: '',
      // Fallback storico: origin + '/' esplicito (non stringa vuota).
      originFallbackPath: '/',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/disegna',
    portfolioApiPath: 'artifacts/portfolio-disegna',
    renameEnabled: false,
  },
  'svg-edit': {
    key: 'svg-edit',
    label: 'SVG-Edit',
    openUrl: {
      envKeys: ['VITE_SVG_EDIT_APP_URL', 'VITE_SVGEDIT_APP_URL', 'VITE_SVG_EDIT_URL'],
      path: '',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/svg-edit',
    portfolioApiPath: 'artifacts/portfolio-svg-edit',
    renameEnabled: false,
    exportable: true,
  },
  qns: {
    key: 'qns',
    label: 'QNS',
    openUrl: {
      // QNS non ha mai avuto un bottone "Apri in QNS" in MyArtifacts;
      // la config esiste solo per completezza/futuro, vedi studentOpenUrlEnabled.
      envKeys: ['VITE_QNS_APP_URL', 'VITE_QNS_URL'],
      path: '',
    },
    studentOpenUrlEnabled: false,
    sessionApiPath: 'qns/artifacts',
    portfolioApiPath: 'artifacts/portfolio-qns',
    renameEnabled: false,
  },
  techyblocks: {
    key: 'techyblocks',
    label: 'TechyBlocks',
    openUrl: {
      envKeys: ['VITE_TECHYBLOCKS_URL', 'VITE_TECHYBLOCKS_APP_URL'],
      path: '/editor.html',
      // originFallbackPath omesso: identico a `path` (comportamento storico,
      // sia getTechyblocksOpenUrl che getAppOpenUrl di TeacherArtifacts
      // appendono /editor.html anche quando la base è l'origin).
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/techyblocks',
    portfolioApiPath: 'artifacts/portfolio-tb',
    renameEnabled: false,
    openInNewTab: false,
    // Prima app sul contratto di assegnazione (migr. 108 + artifacts-assignment.ts).
    assignable: true,
    assignmentApiPath: 'sessions',
    bootstrapApiPath: 'artifacts/techyblocks/bootstrap',
  },
  scrivi: {
    key: 'scrivi',
    label: 'Scrivi',
    openUrl: {
      envKeys: ['VITE_SCRIVI_APP_URL', 'VITE_SCRIVI_URL'],
      path: '',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/scrivi',
    portfolioApiPath: 'artifacts/portfolio-scrivi',
    renameEnabled: false,
  },
  calcola: {
    key: 'calcola',
    label: 'Calcola',
    openUrl: {
      envKeys: ['VITE_CALCOLA_APP_URL', 'VITE_CALCOLA_URL'],
      path: '',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/calcola',
    portfolioApiPath: 'artifacts/portfolio-calcola',
    renameEnabled: false,
  },
  'circuiti-sim': {
    key: 'circuiti-sim',
    label: 'Circuiti',
    openUrl: {
      envKeys: ['VITE_CIRCUITI_SIM_APP_URL', 'VITE_CIRCUITI_SIM_URL'],
      path: '',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/circuiti-sim',
    portfolioApiPath: 'artifacts/portfolio-circuiti-sim',
    renameEnabled: true,
  },
  // "Il mio libro" (tl4k-book-creator): l'app apre l'artefatto dal query
  // param (artifact_id / teacher_artifact_id) e mostra il libro salvato;
  // senza artefatto valido resta sulla home (deep-link gestito dall'app).
  'book-creator': {
    key: 'book-creator',
    label: 'Il mio libro',
    openUrl: {
      envKeys: ['VITE_BOOK_CREATOR_APP_URL', 'VITE_BOOKCREATOR_APP_URL'],
      path: '',
      originFallbackPath: '/',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/book-creator',
    portfolioApiPath: 'artifacts/portfolio-book-creator',
    renameEnabled: false,
  },
  'mo-mappe': {
    key: 'mo-mappe',
    label: 'MoMappe',
    openUrl: {
      envKeys: ['VITE_MO_MAPPE_APP_URL'],
      path: '',
      originFallbackPath: '/',
    },
    studentOpenUrlEnabled: true,
    sessionApiPath: 'artifacts/mo-mappe',
    portfolioApiPath: 'artifacts/portfolio-mo-mappe',
    renameEnabled: false,
  },
  // Pixel Art (vanilla JS): salvataggio esplicito "Salva" in app, sessione
  // figlia 'pixel-art' derivata dall'incontro. L'app non apre ancora un
  // artefatto da ?artifact_id=: link "Apri in" spento finche' non lo fa.
  'pixel-art': {
    key: 'pixel-art',
    label: 'Pixel Art',
    openUrl: {
      envKeys: ['VITE_PIXEL_ART_APP_URL'],
      path: '',
    },
    studentOpenUrlEnabled: false,
    sessionApiPath: 'artifacts/pixel-art',
    portfolioApiPath: 'artifacts/portfolio-pixel-art',
    renameEnabled: false,
  },
  // Story Dice: "Salva storia" in app (testo del diario + dadi). Come
  // pixel-art, nessun deep link di apertura per ora.
  'story-dice': {
    key: 'story-dice',
    label: 'Story Dice',
    openUrl: {
      envKeys: ['VITE_STORY_DICE_APP_URL'],
      path: '',
    },
    studentOpenUrlEnabled: false,
    sessionApiPath: 'artifacts/story-dice',
    portfolioApiPath: 'artifacts/portfolio-story-dice',
    renameEnabled: false,
  },
};

/** Elenco per i `<select>` dei tipi (derivato dal registro: non può più
 *  disallinearsi da `ArtifactTypeKey` come accadeva con l'array a mano). */
/** Id shortcut TechyWeb per ogni tipo artefatto (deep link `?app=`). */
const ARTIFACT_TO_SHORTCUT: Record<ArtifactTypeKey, string> = {
  wordcloud: 'wordcloud',
  scratchjr: 'scratchjr',
  spirografo: 'spirografo',
  disegna: 'disegna',
  'svg-edit': 'svg_edit',
  qns: 'qns',
  techyblocks: 'techyblocks',
  scrivi: 'scrivi',
  calcola: 'calcola',
  'circuiti-sim': 'circuiti_sim',
  'book-creator': 'tl4k_book_creator',
  'mo-mappe': 'mo-mappe',
  'pixel-art': 'pixel_art',
  'story-dice': 'story_dice',
};

/** Restituisce l'id shortcut TechyWeb per un tipo artefatto (deep link). */
export function artifactTypeToShortcutId(artifactType: ArtifactTypeKey): string {
  return ARTIFACT_TO_SHORTCUT[artifactType] ?? artifactType;
}

export const ARTIFACT_TYPE_OPTIONS: { value: ArtifactTypeKey; label: string }[] =
  (Object.keys(ARTIFACT_REGISTRY) as ArtifactTypeKey[]).map((key) => ({
    value: key,
    label: ARTIFACT_REGISTRY[key].label,
  }));

/** Type guard: distingue una chiave nota da una stringa qualsiasi (es.
 *  `artifact_type` letto da un payload di rete, non tipato a priori). */
export function isKnownArtifactType(value: string): value is ArtifactTypeKey {
  return Object.prototype.hasOwnProperty.call(ARTIFACT_REGISTRY, value);
}

/**
 * Lookup nel registro. Un tipo sconosciuto è la stessa classe di bug già
 * risolta per il default silenzioso 'wordcloud' in MyArtifacts: qui non si
 * fallisce in silenzio, si logga rumorosamente cosa si sta assumendo e si
 * ricade sulla entry indicata da `fallback` (default 'circuiti-sim', che è
 * anche il fallback storico di getAppOpenUrl in TeacherArtifacts.tsx).
 */
export function getArtifactDefinition(
  artifactType: string,
  fallback: ArtifactTypeKey = 'circuiti-sim',
): ArtifactTypeDefinition {
  if (isKnownArtifactType(artifactType)) {
    return ARTIFACT_REGISTRY[artifactType];
  }
  console.warn(
    `[artifactRegistry] tipo artefatto sconosciuto "${artifactType}": ` +
      `nessuna entry nel registro. Uso il fallback "${fallback}" — ` +
      'se questa è una nuova app, aggiungi una entry a ARTIFACT_REGISTRY ' +
      'in packages/admin-ui/src/artifactRegistry.ts invece di lasciare che ' +
      'ricada silenziosamente su un\'altra app.',
  );
  return ARTIFACT_REGISTRY[fallback];
}

function getViteEnv(): Record<string, string | undefined> {
  // import.meta.env non esiste in ambienti non-Vite (es. test node): guardia
  // esplicita invece di lasciar esplodere l'accesso a import.meta.
  const metaEnv = typeof import.meta !== 'undefined'
    ? ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {})
    : {};
  // In test (vitest) `vi.stubEnv` scrive su process.env, non su
  // import.meta.env: fallback esplicito per restare testabile. Non introduce
  // un ramo diverso in build reali — in bundle browser `process` non esiste,
  // e la guardia lo esclude prima di leggerlo.
  const processEnv = typeof process !== 'undefined'
    ? (process.env as Record<string, string | undefined>)
    : {};
  return { ...processEnv, ...metaEnv };
}

function getOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

/**
 * Costruisce l'URL "apri nell'app" per un artefatto, dato il tipo e il nome
 * del query param (`teacher_artifact_id` per TeacherArtifacts, `artifact_id`
 * per MyArtifacts). Precedenza env var → fallback origin, byte-identica al
 * comportamento storico dei due componenti (vedi commenti in
 * ARTIFACT_REGISTRY per le eccezioni per-app).
 */
export function buildArtifactOpenUrl(
  artifactId: string,
  paramName: string,
  config: ArtifactOpenUrlConfig,
): string {
  const env = getViteEnv();
  const origin = getOrigin();
  const resolvedBase = config.envKeys
    .map((k) => env?.[k])
    .find((v): v is string => typeof v === 'string' && v.trim().length > 0);

  if (resolvedBase) {
    const trimmed = resolvedBase.trim().replace(/\/$/, '');
    return `${trimmed}${config.path}?${paramName}=${encodeURIComponent(artifactId)}`;
  }

  const fallbackPath = config.originFallbackPathEnvKey
    ? env?.[config.originFallbackPathEnvKey] || config.originFallbackPath || config.path
    : config.originFallbackPath ?? config.path;

  return `${origin}${fallbackPath}?${paramName}=${encodeURIComponent(artifactId)}`;
}
